import React, { useRef, useEffect } from "react";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import { Check, Plus } from "lucide-react";
import { List } from "../../types";
import { useSharedListIds } from "../../hooks/queries/useSharedListIds";
import SharedListMark from "../ui/SharedListMark";

interface ListPopoverProps {
  lists: List[];
  selectedListId: string | null | undefined;
  onSelectList: (list: List) => void;
  onClearList?: () => void;
  onAddList: () => void;
  onClose: () => void;
  anchorEl?: HTMLElement | null;
}

export const ListPopover: React.FC<ListPopoverProps> = ({
  lists,
  selectedListId,
  onSelectList,
  onClearList,
  onAddList,
  onClose,
  anchorEl,
}) => {
  const noListSelected = selectedListId == null;
  const popoverRef = useRef<HTMLDivElement>(null);
  const { data: sharedListIds } = useSharedListIds();

  useEscapeKey(onClose);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        !(anchorEl && anchorEl.contains(e.target as Node))
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose, anchorEl]);

  return (
    <div
      ref={popoverRef}
      role="listbox"
      aria-label="Select list"
      className="absolute top-full left-0 right-0 mt-1.5 z-50 bg-warm-bg-card border border-warm-border rounded-lg shadow-xl py-1 animate-popover-in origin-top-left"
    >
      <div className="max-h-[240px] overflow-y-auto">
        {onClearList && (
          <button
            key="__no-list__"
            role="option"
            aria-selected={noListSelected}
            onClick={() => {
              onClearList();
              onClose();
            }}
            className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors text-left ${
              noListSelected
                ? "bg-warm-blue/10 text-warm-text-primary"
                : "text-warm-text-primary hover:bg-warm-bg-elevated"
            }`}
          >
            <span className="text-base leading-none">📭</span>
            <span className="flex-1">No list</span>
            {noListSelected && (
              <Check size={14} className="text-warm-blue flex-shrink-0" />
            )}
          </button>
        )}
        {lists.map((l) => {
          const isSelected = l.id === selectedListId;
          return (
            <button
              key={l.id}
              role="option"
              aria-selected={isSelected}
              onClick={() => {
                onSelectList(l);
                onClose();
              }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors text-left ${
                isSelected
                  ? "bg-warm-blue/10 text-warm-text-primary"
                  : "text-warm-text-primary hover:bg-warm-bg-elevated"
              }`}
            >
              <span className="text-base leading-none">{l.emoji}</span>
              <span className="flex-1">{l.name}</span>
              {sharedListIds?.has(l.id) && <SharedListMark size={11} />}
              {isSelected && (
                <Check size={14} className="text-warm-blue flex-shrink-0" />
              )}
            </button>
          );
        })}
      </div>
      <div className="border-t border-warm-border mt-1 pt-1">
        <button
          onClick={() => {
            onClose();
            onAddList();
          }}
          className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-warm-text-tertiary hover:text-warm-blue hover:bg-warm-bg-elevated transition-colors text-left"
        >
          <Plus size={14} />
          Add new list
        </button>
      </div>
    </div>
  );
};
